import { type Dict, dictToList } from "@utils/dictUtils";
import type { Country as COUNTRY } from "@generated/model/country";
import type { ContentMetadata_SUMMARY } from "@newgenerated/shared/schema";
import { Language } from "@newgenerated/shared/schema";
import { assert } from "@utils/assertion";

export { Language } from "@newgenerated/shared/schema";

export { DateTime } from "luxon";
export namespace LanguageHelper {
  const values = Language.getValues();
  export type Impl = (typeof values)[number];
  export type LanguageMap<T> = { [L in Impl]?: T };

  export function getValues(): typeof values {
    return values;
  }

  export function getLanguageId(language: Impl): number {
    return values.indexOf(language) + 1;
  }

  export function getLangByIsoCode(lang: string): Impl {
    const language = values.find((value) => value === lang.toLowerCase());
    return language ?? "en";
  }

  export function parseLangByIsoCode(lang: string): Impl {
    const language = values.find((value) => value === lang.toLowerCase());
    assert(language !== undefined, `Invalid ISO code passed to the parser: '${language}'`);
    return language;
  }

  export function getUrlLanguage(url: string): Impl | null {
    const urlLang = values.find((value) => value === url.replace(/^\/([^\/]*).*$/, "$1").toLowerCase());
    return urlLang ?? null;
  }

  export function getCurrentLanguage(): Impl {
    const urlLang = getUrlLanguage(window.location.pathname);
    if (urlLang !== null) return urlLang;
    const sessionLang = values.find((value) => value === navigator.language.toLowerCase());
    if (sessionLang !== undefined) return sessionLang;
    return "en";
  }

  export function isLanguage(lang: string): boolean {
    const language = values.find((value) => value === lang.toLowerCase());
    return language !== undefined;
  }

  export function parseLanguageMap<T>(dict: Dict<T>, valueParser: (value: unknown) => T): LanguageMap<T> {
    return Object.assign(
      {},
      ...dictToList(dict).map((entry: [string, unknown]) => {
        const partial: Partial<Record<Impl, T>> = {};
        partial[getLangByIsoCode(entry[0])] = valueParser(entry[1]);
        return partial;
      }),
    );
  }

  export function renderLanguageMap<T, RenderedT>(map: LanguageMap<T>, valueRenderer: (value: T) => RenderedT): Dict<RenderedT> {
    const r: { [key: string]: RenderedT } = {};
    dictToList<T>(map).forEach(([key, val]) => {
      if (val !== undefined) {
        r[key] = valueRenderer(val);
      }
    });
    return r;
  }

  export function getTmsKey(language: Impl): string {
    // keys can't be dynamically created, because of the TmsTxIdsAggregator
    switch (language) {
      case "en":
        return "general:language.en";
      case "de":
        return "general:language.de";
      case "es":
        return "general:language.es";
      case "ru":
        return "general:language.ru";
      case "zh":
        return "general:language.zh";
      case "pt":
        return "general:language.pt";
      case "fr":
        return "general:language.fr";
      case "ar":
        return "general:language.ar";
    }
  }
}
export type Page<T> = {
  items: T[];
  totalCount: number | bigint;
  page: number;
  pageSize: number;
};

export type AdminToolTyp = "internal-admin" | "portal-admin";

export namespace Currency {
  const values = ["CHF", "CNY", "USD", "EUR", "GBP", "INR", "JPY"] as const;
  export type Impl = (typeof values)[number];

  export function getValues(): typeof values {
    return values;
  }
}
export type Currency = Currency.Impl;

export namespace CurrencyDeprecated {
  const values = [...Currency.getValues(), "RUB", "MILES"] as const;
  export type Impl = (typeof values)[number];

  export function getValues(): typeof values {
    return values;
  }
}
export type Country = {
  countryCode: string;
  currency: Currency;
  region: COUNTRY["region"];
  names: Record<Language, string>;
};

export type SummaryPageResponse = {
  items: ContentMetadata_SUMMARY[];
  page: number;
  psize: number;
  count: number;
  countTotal: number;
};

export type DropdownEntry = {
  value: string;
  label: string;
  disabled?: boolean;
};
